import { Flex, Text } from '@chakra-ui/react'
import { memo } from 'react'
import type { FilterMode } from '../../../types/filters.types'

interface FilterModeToggleProps {
  mode: FilterMode
  onChange: (mode: FilterMode) => void
  isDisabled?: boolean
  'aria-label'?: string
}

const OPTIONS: { value: FilterMode; label: string }[] = [
  { value: 'include', label: 'Include' },
  { value: 'exclude', label: 'Exclude' },
]

/**
 * Two-segment Include|Exclude switch shown beside a filter field's label.
 *
 * Include and exclude are mutually exclusive for a single field: selecting
 * B1+B2 already yields only B1+B2, so also excluding B3 would be a no-op.
 * Switching mode therefore swaps which value slot the field edits and which
 * query param it emits — it never sends both.
 */
const FilterModeToggle = ({ mode, onChange, isDisabled, 'aria-label': ariaLabel }: FilterModeToggleProps) => {
  return (
    <Flex
      role="radiogroup"
      aria-label={ariaLabel ?? 'Filter mode'}
      bg="neutral.50"
      borderRadius="full"
      p="2px"
      gap="2px"
      opacity={isDisabled ? 0.5 : 1}
      pointerEvents={isDisabled ? 'none' : 'auto'}
      flexShrink={0}
    >
      {OPTIONS.map((option) => {
        const isSelected = mode === option.value
        return (
          <Text
            key={option.value}
            as="button"
            type="button"
            role="radio"
            aria-checked={isSelected}
            disabled={isDisabled}
            onClick={() => {
              if (!isSelected) onChange(option.value)
            }}
            px="10px"
            py="3px"
            borderRadius="full"
            fontSize="11px"
            fontWeight={isSelected ? '500' : '400'}
            transition="0.15s ease-out"
            cursor={isSelected ? 'default' : 'pointer'}
            bg={isSelected ? 'white' : 'transparent'}
            color={isSelected ? 'primary.500' : 'neutral.500'}
            boxShadow={isSelected ? '0px 1px 2px 0px #00000014' : 'none'}
            _hover={isSelected ? undefined : { color: 'neutral.700' }}
          >
            {option.label}
          </Text>
        )
      })}
    </Flex>
  )
}

export default memo(FilterModeToggle)
